import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Drawer,
  List,
  ListItem,
  Divider,
  Box,
  Backdrop,
  Stack,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { ReactComponent as DxaiHeaderLogo } from "../assets/DxaiHeaderLogo.svg"; // Import SVG
import { ReactComponent as Procuts } from "../assets/Procuts.svg"; // Import SVG
import { ReactComponent as Partner } from "../assets/Partner.svg"; // Import SVG
import { ReactComponent as Aboutus } from "../assets/Aboutus.svg"; // Import SVG
import { ReactComponent as LinkedIn } from "../assets/Linkedin.svg"; // Import SVG
import { ReactComponent as Instagram } from "../assets/Instagram.svg"; // Import SVG
import { scrollToElement } from "../helper";
import { useNavigate } from "react-router-dom";

export default function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const navigate = useNavigate();

  // Function to handle scrolling and route redirection
  const handleScrollToElement = (sectionId) => {
    // Check if the current route is the home page
    if (window.location.pathname === "/") {
      // If on home page, just scroll
      scrollToElement(sectionId);
    } else {
      // If not on the home page, navigate to home and then scroll
      navigate("/");
      setTimeout(() => {
        scrollToElement(sectionId);
      }, 500); // Allow time for the route change before scrolling
    }
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{ backgroundColor: "white", color: "black", boxShadow: "none" }}
      >
        <Toolbar sx={{ justifyContent: "center" }}>
          <Box
            sx={{
              width: "90%", // Set content width to 80% of AppBar
              display: "flex",
              alignItems: "center",
              pt: 2,
              justifyContent: "space-between", // Ensure elements spread out
            }}
          >
            {/* ✅ Properly Aligned Logo */}
            <Box
              sx={{
                height: { xs: "30px", sm: "34px", md: "40px" }, // Responsive height
                width: { xs: "100px", sm: "120px", md: "150px" }, // Responsive width
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start", // Ensure it aligns to the left
              }}
              onClick={() => navigate("/")}
            >
              <DxaiHeaderLogo
                style={{
                  height: "100%", // Ensures it takes parent's height
                  width: "100%", // Ensures it takes parent's width
                  objectFit: "contain", // Prevents stretching
                }}
              />
            </Box>

            {/* ✅ Navigation Buttons (Visible Only on Larger Screens) */}
            <Box sx={{ display: { xs: "none", md: "flex" }, gap: 2 }}>
              <Button
                color="inherit"
                onClick={() => handleScrollToElement("intro-app")}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: "600",
                    fontFamily: "Poppins",
                    color: "rgba(35, 97, 223, 1)",
                  }}
                >
                  About Us
                </Typography>
              </Button>
              <Button
                color="inherit"
                onClick={() => handleScrollToElement("products")}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: "600",
                    fontFamily: "Poppins",
                    color: "rgba(35, 97, 223, 1)",
                  }}
                >
                  Products
                </Typography>
              </Button>
              <Button
                color="inherit"
                onClick={() => handleScrollToElement("about-us")}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: "600",
                    fontFamily: "Poppins",
                    color: "rgba(35, 97, 223, 1)",
                  }}
                >
                  Partner With Us
                </Typography>
              </Button>
            </Box>

            {/* ✅ Menu Button (Visible on Mobile) */}
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              sx={{ display: { xs: "block", md: "none" } }}
              onClick={toggleDrawer(true)}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box
          sx={{ width: "60vw" }}
          role="presentation"
          onClick={toggleDrawer(false)}
        >
          <List>
            <ListItem sx={{ mt: 1, mb: 2 }}>
              <Box
                sx={{
                  height: { xs: "30px", sm: "34px", md: "40px" }, // Responsive height
                  width: { xs: "100px", sm: "120px", md: "150px" }, // Responsive width
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start", // Ensure it aligns to the left
                }}
                onClick={() => navigate("/")}
              >
                <DxaiHeaderLogo
                  style={{
                    height: "100%", // Ensures it takes parent's height
                    width: "100%", // Ensures it takes parent's width
                    objectFit: "contain", // Prevents stretching
                  }}
                />
              </Box>
            </ListItem>
            <Divider />
            <ListItem
              button
              sx={{
                pl: 5,
                pt: 2,
                pb: 2,
                mt: 2,
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                onClick={() => {
                  handleScrollToElement("products");
                }}
              >
                <Box>
                  <Procuts />
                </Box>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: "600",
                    fontFamily: "Poppins",
                  }}
                >
                  Products
                </Typography>
              </Stack>
            </ListItem>
            <ListItem
              button
              sx={{
                pl: 5,
                pt: 2,
                pb: 2,
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                onClick={() => {
                  handleScrollToElement("about-us");
                }}
              >
                <Box>
                  <Partner />
                </Box>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: "600",
                    fontFamily: "Poppins",
                  }}
                >
                  Partner With Us
                </Typography>
              </Stack>
            </ListItem>
            <ListItem
              button
              sx={{
                pl: 5,
                pt: 2,
                pb: 2,
                mb: 2,
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                onClick={() => {
                  handleScrollToElement("intro-app");
                }}
              >
                <Box>
                  <Aboutus />
                </Box>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: "600",
                    fontFamily: "Poppins",
                  }}
                >
                  About Us
                </Typography>
              </Stack>
            </ListItem>
          </List>
          <Divider />
          <Stack direction="row" spacing={3} sx={{ pt: 4, ml: 6 }}>
            <Box
              onClick={() =>
                window.open(
                  "https://www.linkedin.com/company/dxai-healthtech/about/",
                  "_blank"
                )
              }
            >
              <LinkedIn />
            </Box>
            <Box
              onClick={() =>
                window.open("https://www.instagram.com/dxai.life/", "_blank")
              }
            >
              <Instagram />
            </Box>
          </Stack>
        </Box>
      </Drawer>

      {drawerOpen && (
        <Backdrop
          open={drawerOpen}
          onClick={toggleDrawer(false)}
          sx={{ zIndex: 1 }}
        />
      )}
    </>
  );
}
